import {get} from '../js/object';

const FIELD_STATUS = {
  OPTIONAL: 'OPTIONAL',
  MANDATORY: 'MANDATORY'
};

/**
 * @private
 * @desc Gets subdivision type based off selected country
 * @param {String} selectedCountryCode
 * @returns {String} 'state' || 'province' || 'county'
 */
const subdivisionType = (selectedCountryCode='') => {
  switch(selectedCountryCode) {
    case'USA':
    case'US': {
      return 'state';
    }
    case'CAN':
    case'CA': {
      return 'province';
    }
    default:
      return 'county'; 
  }
};

/**
 * @name getCountryFieldRules
 * @desc Gets the selected country's rules about fields of subdivisions, subdivision types(eg: state, county...), postal and license issue/expiry dates  
 * @param {Array} countries
 * @param {String} selectedCountryCode
 * @returns {Object} {
 *   enablePostal: {Boolean},
 *   enableSubdivisions: {Boolean},
 *   subdivisionType: {String},
 *   showExpirationDate: {Boolean},
 *   isIssueDateOptional: {Boolean},
 *   isExpirationDateOptional: {Boolean}
 * }
 * @memberof utils
 * @example
 * utils.getCountryFieldRules({countries, selectedCountryCode: 'US'});
 */
export function getCountryFieldRules({countries=[], selectedCountryCode='US'}) {
  const isISO3CountryCode = selectedCountryCode.length === 3;
  const selectedCountryData = countries.find(country =>
    isISO3CountryCode 
      ? country.country_iso3_code === selectedCountryCode 
      : country.country_code === selectedCountryCode
  );
  const enablePostal = !!get(selectedCountryData, 'country_content.postal_code_type');
  const enableSubdivisions = !!get(
    selectedCountryData,
    'country_content.enable_country_sub_division'
  );

  const { country_content = {} } = selectedCountryData || {};
  const { license_issue_date, license_expiry_date } = country_content;

  const isIssueDateOptional = license_issue_date === FIELD_STATUS.OPTIONAL;
  const isExpirationDateOptional = license_expiry_date === FIELD_STATUS.OPTIONAL;

  const showIssueDate = license_issue_date === FIELD_STATUS.MANDATORY || isIssueDateOptional;
  const showExpirationDate = license_expiry_date === FIELD_STATUS.MANDATORY || isExpirationDateOptional;

  return {
    enablePostal,
    enableSubdivisions,
    subdivisionType: subdivisionType(selectedCountryCode),
    showIssueDate,
    showExpirationDate,
    isIssueDateOptional,
    isExpirationDateOptional
  };
}
